import { Injectable } from '@angular/core';
import { combineLatest, forkJoin, Observable, of, throwError } from 'rxjs';
import { catchError, exhaustMap, mergeMap, tap } from 'rxjs/operators';
import { HttpEvent, HttpEventType } from '@angular/common/http';
import {
  REST_DRIVE_FILES,
  REST_DRIVE_FILES_PRESIGNED,
  REST_DRIVE_VIEW_EXTERNAL_DRIVE,
} from '../restApi/RestRoutes';
import { RestRequestService } from '../restApi/rest-request.service';
import {
  FileConstantTotals,
  PresignedUrlResp,
  FileUploadResp,
  FileUploadStatus,
  UploadMetaData,
  UploadStatusMap,
  FileUploadTotals,
  FILE_VALIDATION_STATUS,
  ManagedFile,
  isPendingFile,
  isDeletedFile,
} from '@app/framework/interfaces/file.type';
import { NotificationV2Service } from '@app/notification-v2.service';
import { DriveApiService } from '@app/services/drive-api.service';

@Injectable({
  providedIn: 'root',
})
export class PresignedFileUploadService {
  constructor(
    private restService: RestRequestService,
    private notif: NotificationV2Service,
    private driveApiService: DriveApiService,
  ) {}

  /** current upload status for each separate upload in progress */
  private uploadStatus: UploadStatusMap = {};
  /** totals that are constant while uploading multiple files, total size and file count */
  private constantTotals: FileConstantTotals = { totalBytes: 0, fileCount: 0 };

  /**
   * Get a presigned S3 url for a file upload with given metadata
   * @param metaData - used to configure the file upload
   * @param uuid - external drive uuid
   */
  private initiateFileUpload(
    metaData: UploadMetaData,
    uuid: string = null,
  ): Observable<PresignedUrlResp> {
    return this.restService.postWithObservable(
      uuid
        ? `${REST_DRIVE_VIEW_EXTERNAL_DRIVE}/${uuid}/user-drive-files/presigned`
        : REST_DRIVE_FILES_PRESIGNED,
      metaData,
    );
  }

  /**
   * Actually upload a file to S3 using a presigned url and reporting progress meanwhile.
   * @param file - the file to upload
   * @param presignedUrl - the presigned url to upload the file to
   */
  private uploadFileToPresignedUrl(presignedUrl: string, file: Blob): Observable<HttpEvent<any>> {
    return this.restService
      .putWithObservableReportProgress(presignedUrl, file, {
        Authorization: 'none',
        'Content-Type': file.type,
        'ngsw-bypass': 'true', // ngsw-bypass is needed to bypass angular service worker
      })
      .pipe(
        catchError((err) => {
          console.error(err);
          return throwError(() => err);
        }),
      );
  }

  /**
   * Validate a file upload - backend needs this step after the upload to update its records.
   * @param id - the id of the file to validate
   * @param uuid - optional, external drive uuid, validate uploaded file in external drive
   */
  private validateFileUpload(id: number, uuid: string = null) {
    return this.restService.patchWithObservable(
      uuid
        ? `${REST_DRIVE_VIEW_EXTERNAL_DRIVE}/${uuid}/user-drive-files/validate/${id}`
        : `${REST_DRIVE_FILES}/validate/${id}`,
      {},
    );
  }

  /**
   * Function to upload a file to S3 using a presigned url. It handles the full upload process, updating global upload status. <br/>
   * It follows the following steps:
   * 1. Get a presigned url to S3 for a file upload with given metadata
   * 2. Upload a file to S3 using a presigned url
   * 3. Validate a file upload - backend needs to confirm the upload
   * @param file - the file to upload
   * @param metadata - object containing information about the file, e.g. it is profile picture, commitment attachment, etc.
   * @param uuid - optional, if specified upload to external drive with given uuid
   */
  private handleS3FileUpload(
    file: File,
    metadata: UploadMetaData,
    uuid: string = null,
  ): Observable<FileUploadResp | HttpEvent<FileUploadResp>> {
    let uploadPathData: PresignedUrlResp;

    return this.initiateFileUpload(metadata, uuid).pipe(
      exhaustMap((data) => {
        uploadPathData = data;
        const blobData = new Blob([file], { type: file.type });
        return combineLatest([
          of({ id: data.file_id }),
          this.uploadFileToPresignedUrl(uploadPathData.presigned_url, blobData),
        ]);
      }),
      mergeMap(([fileData, fileStatus]) => {
        this.setFileUploadStatus(fileData.id, fileStatus, FILE_VALIDATION_STATUS.PENDING);

        if (fileStatus.type === HttpEventType.Response && fileStatus.ok) {
          // if file uploaded, validate it - backend needs to update its records
          return this.validateFileUpload(uploadPathData.file_id, uuid).pipe(
            tap(() => {
              this.setFileUploadStatus(fileData.id, fileStatus, FILE_VALIDATION_STATUS.SUCCESS);
            }),
            catchError((err) => {
              this.setFileUploadStatus(fileData.id, fileStatus, FILE_VALIDATION_STATUS.ERROR);
              return throwError(() => err);
            }),
          );
        }

        // if not completed just return current status
        return of(fileStatus);
      }),
      catchError((err) => {
        console.error(err, metadata, file);

        // if we couldn't get a presigned url, set a random id for the file and a made-up error status
        // we create a fake response error because we didn't get a real response on upload progress,
        // because we don't have a presigned url to upload to
        this.setFileUploadStatus(
          `no_file_id_${Math.round(Math.random() * 1000)}`,

          { type: HttpEventType.Response, status: 400, ok: false } as HttpEvent<any>,
          FILE_VALIDATION_STATUS.ERROR,
        );
        return throwError(() => err);
      }),
    );
  }

  private getProgressMessage(totals: FileUploadTotals = this.getTotals()): string {
    if (!totals) {
      return 'Uploading...';
    }

    const percentage = totals.percent.toFixed(0);
    const uploaded = totals.uploadedCount;
    const total = totals.fileCount;

    let message = `Uploaded ${percentage}%`;
    if (total > 1) {
      message += ` (${uploaded} of ${total})`;
    }
    if (totals.errorCount > 0) {
      message += `, ${totals.errorCount} failed`;
    }
    if (uploaded === total) {
      message += ', finishing up...';
    }

    return message;
  }

  /**
   * Upload a single file to S3 with progress notifications and state management <br/>
   * Returns all upload events during upload, emits IFileUploadResponse on close. <br/>
   * @param file - the file to upload
   * @param metadata - object containing information about the file, e.g. it is profile picture, commitment attachment, etc.
   * @param uuid - external drive uuid, if specified upload to external drive
   * @param handleNotifications - True by default. If left on, this method handles upload progress notifications
   * Success notification is not shown, only error notification on failure.
   */
  uploadFileToS3(
    file: File,
    metadata: UploadMetaData,
    uuid: string = null,
    handleNotifications = true,
  ): Observable<FileUploadResp | HttpEvent<any>> {
    if (!file || !metadata) {
      return throwError(() => new Error('Could not start upload, missing file or metadata'));
    }

    this.resetUploadState();
    this.updateConstantTotals(file.size, 1);

    if (!metadata.type) {
      // if no type is available, use this as default,
      // otherwise S3 will reject the upload, and we won't get a presigned URL
      metadata.type = 'application/octet-stream';
    }

    let notifHash: string;
    if (handleNotifications) {
      notifHash = this.notif.showLoading('Uploading...');
    }

    // add default notification if no parent notification hash is provided
    return this.handleS3FileUpload(file, metadata, uuid).pipe(
      tap((event) => {
        const totals = this.getTotals();
        if ((event as HttpEvent<FileUploadResp>)?.type === HttpEventType.UploadProgress) {
          const message = this.getProgressMessage(totals);
          this.notif.updateMessage(notifHash, message);
        }

        // checks if all chunks are uploaded
        if (totals.completed) {
          this.notif.close(notifHash);
        }
      }),
      catchError((err) => {
        if (handleNotifications) {
          this.notif.close(notifHash);
          this.notif.showError('An error occurred during the upload.');
        }
        return throwError(() => err);
      }),
    );
  }

  /**
   * Upload multiple files to S3 using a presigned url, return only when all requests have finished <br/>
   * Note: that this component won't emit an error observable, only the error object in the response array. <br/>
   * Use if you want to use the upload and see only the result
   * @param files - the files to upload
   * @param metadata - the metadata to use for the file upload
   * @param uuid - external drive uuid, if specified upload to external drive
   * @param showNotifs - if it is called in sequence to upload folders and their content (every folder is uploaded separately)
   */
  uploadMultipleFilesToS3(
    files: File[],
    metadata: Partial<UploadMetaData>[],
    uuid: string = null,
    showNotifs = true,
  ): Observable<FileUploadResp[]> {
    if (!files?.length || files === null || files === undefined) {
      return of([]);
    }
    this.resetUploadState();
    const fileUploadRequests: Observable<FileUploadResp>[] = [];
    let notifHash: string = null;
    if (showNotifs) {
      notifHash = this.notif.showLoading('Uploading...');
    }
    const size = files.reduce((acc, file) => acc + file.size, 0);
    this.updateConstantTotals(size, files.length);
    files.forEach((file, index) => {
      const fileMetadata: UploadMetaData = {
        name: file.name,
        size: Number(file.size),
        type: file.type,
        ...metadata[index],
      };

      fileUploadRequests.push(
        this.handleS3FileUpload(file as File, fileMetadata, uuid).pipe(
          tap((event) => {
            if ((event as HttpEvent<FileUploadResp>)?.type === HttpEventType.UploadProgress) {
              const message = this.getProgressMessage();
              this.notif.updateMessage(notifHash, message);
            }
          }),
          catchError((error) => {
            console.warn(error, fileMetadata, file);
            return of(error);
          }),
        ),
      );
    });
    return forkJoin(fileUploadRequests).pipe(
      tap(() => {
        if (!showNotifs) {
          return;
        }
        this.notif.close(notifHash);
        const totals = this.getTotals();

        if (totals.completedWithError) {
          this.notif.showError(
            `${totals.uploadedCount} files uploaded successfully, ${totals.errorCount} failed.`,
          );
        } else if (totals.fileCount === 1) {
          this.notif.showSuccess(`File uploaded successfully.`);
        } else {
          this.notif.showSuccess(`All ${totals.fileCount} files uploaded successfully.`);
        }
      }),
    );
  }

  /**
   * Handle upload of new files and deletion of deleted files in a single method <br/>
   * Returns when all upload and delete requests have finished <br/>
   * Useful when handling multiple files associated to a single entity, e.g. commitment attachments <br/>
   * Note: that this component won't emit an error observable, only the error object in the response array. <br/>
   * @param filesToManage - array of files to upload or delete. Files without id will be uploaded, files with toBeDeleted flag will be deleted, others will be ignored
   * @param metadata - this metadata will be applied to all new files being uploaded
   */
  handleUploadAndDelete(
    filesToManage: ManagedFile[],
    metadata: Partial<UploadMetaData>,
  ): Observable<FileUploadResp[]> {
    if (!filesToManage?.length) {
      return of([]);
    }

    const newFiles = filesToManage.filter(isPendingFile);
    const deletedFiles = filesToManage.filter(isDeletedFile);
    const metadataArray = newFiles.map(() => metadata);

    const deleteRequests = deletedFiles.map((file) =>
      this.driveApiService.deleteFile(file.id).pipe(
        catchError((err) => {
          return of(err);
        }),
      ),
    );

    return forkJoin([this.uploadMultipleFilesToS3(newFiles, metadataArray), ...deleteRequests]);
  }

  /**
   * Set upload progress for each file separately, progress is broken down into loaded and total.
   * @param id - id of the file to be uploaded or 'no_file_id_${random_number}' if no id is available
   * @param fileStatus - an HTTPEvent from HTTPClient
   * @param validationStatus - the result of the validation request - it may be in pending if the request is not finished yet
   */
  setFileUploadStatus(
    id: number | string,
    fileStatus: HttpEvent<any>,
    validationStatus: FILE_VALIDATION_STATUS = FILE_VALIDATION_STATUS.PENDING,
  ) {
    if (
      fileStatus.type === HttpEventType.Sent ||
      fileStatus.type === HttpEventType.DownloadProgress ||
      fileStatus.type === HttpEventType.User
    ) {
      // We don't care of these. No need to update progress for these events
      return;
    }

    if (fileStatus.type === HttpEventType.UploadProgress) {
      // if fileStatus is a progress event from Angular HttpClient
      const newStatus: FileUploadStatus = {
        totalBytes: fileStatus.total,
        loadedBytes: fileStatus.loaded,
        completedWithSuccess: false,
        completedWithError: false,
      };
      this.uploadStatus = {
        ...this.uploadStatus,
        [id]: newStatus,
      };
    }

    if (
      fileStatus.type === HttpEventType.Response ||
      fileStatus.type === HttpEventType.ResponseHeader
    ) {
      // if fileStatus is a response object from the server
      const completedWithError =
        !fileStatus.ok ||
        fileStatus.status >= 400 ||
        validationStatus === FILE_VALIDATION_STATUS.ERROR;
      const completedWithSuccess =
        this.uploadStatus[id]?.totalBytes === this.uploadStatus[id]?.loadedBytes &&
        this.uploadStatus[id]?.totalBytes > 0;
      const validated = validationStatus === FILE_VALIDATION_STATUS.SUCCESS;

      const newStatus: FileUploadStatus = {
        ...this.uploadStatus[id],
        completedWithSuccess,
        completedWithError,
        validated,
      };

      this.uploadStatus = {
        ...this.uploadStatus,
        [id]: newStatus,
      };
    }
  }

  getTotals(): FileUploadTotals {
    let total = this.constantTotals.totalBytes;
    let fileCount = this.constantTotals.fileCount;

    if (this.constantTotals.totalBytes === 0 && this.constantTotals.fileCount === 0) {
      // fallback values if init constant totals was not called
      total = Object.values(this.uploadStatus)
        .map((s) => s.totalBytes)
        .reduce((acc, i) => acc + i, 0);

      fileCount = Object.values(this.uploadStatus).length;
    }

    const loaded = Object.values(this.uploadStatus)
      .map((s) => s.loadedBytes || 0)
      .reduce((acc, i) => acc + i, 0);
    const uploadedCount = Object.keys(this.uploadStatus).reduce((acc, key) => {
      if (
        this.uploadStatus[key].loadedBytes === this.uploadStatus[key].totalBytes &&
        this.uploadStatus[key].totalBytes > 0
      ) {
        return acc + 1;
      }
      return acc;
    }, 0);

    const completedCount = Object.keys(this.uploadStatus).reduce((accumulator, key) => {
      if (this.uploadStatus[key].completedWithSuccess) {
        return accumulator + 1;
      }
      return accumulator;
    }, 0);

    const errorCount = Object.keys(this.uploadStatus).reduce((accumulator, key) => {
      if (this.uploadStatus[key].completedWithError) {
        return accumulator + 1;
      }
      return accumulator;
    }, 0);

    let percent = 0;
    if (total !== 0) {
      percent = (loaded / total) * 100;
    }

    const completed = completedCount + errorCount >= fileCount && fileCount > 0;
    const completedWithError = errorCount > 0;

    return {
      percent,
      uploadedCount,
      completed,
      completedWithError,
      errorCount,
      loadedBytes: loaded,
      fileCount,
    };
  }

  /**
   * Clear upload state, for each file and the constant totals too. <br/>
   * Should be called before starting a new upload session.
   */
  resetUploadState() {
    this.uploadStatus = {};
    this.constantTotals = { totalBytes: 0, fileCount: 0 };
  }

  /**
   * Initialize totals for all uploads. <br/>
   * Can be called if upload is in progress, it will add to the current totals. <br/>
   * @param totalBytes - number
   * @param fileCount - number
   */
  updateConstantTotals(totalBytes: number, fileCount: number) {
    this.constantTotals = {
      totalBytes: (this.constantTotals?.totalBytes ?? 0) + totalBytes,
      fileCount: (this.constantTotals?.fileCount ?? 0) + fileCount,
    };
  }
}
